import { useNavigate } from "react-router-dom";
import { Button } from "../../../catalyst-components/button";
import { memo, useContext } from "react";

import { MainRoutes, SuggestedAction, SuggestedActionType, SystemMessages, EvergreenEntryPoints } from "../../../shared/models";
import { useEffect, useState } from "react";
import { ResumeUpload } from "../../../shared/components/ResumeUpload";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { BaseContext } from "../../../shared/base-context";
import { Id } from "../../../convex/_generated/dataModel";

export interface SuggestionsProps {
    suggestions: SuggestedAction[];
    sendMessage: (value: string) => void;
    handleSessionComplete: () => void;
    assistantName?: string;
}
export const Suggestions: React.FC<SuggestionsProps> = memo(({ suggestions, sendMessage, handleSessionComplete, assistantName }) => {
    const navigate = useNavigate();
    const { convexUserId } = useContext(BaseContext);
    const [isLight, setIsLight] = useState(false);
    const [showUploadResumeModal, setShowUploadResumeModal] = useState<boolean>(false);
    const injectContext = useMutation(api.evergreen_chat.injectContext);

    const handleUploadResume = () => {
        setShowUploadResumeModal(true);
    }

    const handleResumeUploaded = (resumeUploaded: boolean) => {
        setShowUploadResumeModal(false);
        if (resumeUploaded) {
            sendMessage(SystemMessages.ResumeUploaded);
        } else {
            sendMessage(SystemMessages.NoResume);
        }
    }

    const handleMoreOnThis = async () => {
        if (!convexUserId) return;
        
        handleSessionComplete();
        
        // Create entry point for session completion
        // The AI will use its tools to find skill info
        const entryPoint = EvergreenEntryPoints.sessionComplete(
            assistantName || "the session",
            "their current skill" // AI will look this up
        );
        
        // Inject context and trigger auto-response
        await injectContext({
            user_id: convexUserId as Id<"users">,
            context_id: entryPoint.contextId,
            system_message: entryPoint.systemMessage,
            auto_submit_prompt: entryPoint.autoSubmitPrompt,
        });
        
        navigate(MainRoutes.MyChat);
    };

    const handleSelection = (suggestion: SuggestedAction) => {
        if (suggestion.action === SuggestedActionType.Submit) { 
            sendMessage(suggestion.content);
        } else if (suggestion.action === SuggestedActionType.Exit) {
            handleSessionComplete();
            navigate(MainRoutes.Progress);
        } else if (suggestion.action === SuggestedActionType.UploadResume) {
            handleUploadResume();
        } else if (suggestion.action === SuggestedActionType.YourChat) {
            handleSessionComplete();
            navigate(MainRoutes.PersonalAssistant);
        } else if (suggestion.action === SuggestedActionType.Next) {
            sendMessage(suggestion.content);
        } else if (suggestion.action === SuggestedActionType.MultipleChoice) {
            sendMessage(suggestion.content);
        } else if (suggestion.action === SuggestedActionType.MoreOnThis) {
            handleMoreOnThis();
        }
    }

    useEffect(() => {
        if (suggestions?.[0]?.action === SuggestedActionType.MultipleChoice || suggestions?.[0]?.action === SuggestedActionType.Submit || suggestions?.[0]?.action === SuggestedActionType.Next) {
            setIsLight(true);
        } else {
            setIsLight(false);
        }
    }, [suggestions]);

    return (
        <>
            <section className="flex flex-row flex-wrap">
                {suggestions.map((suggestion, index) =>
                    <Button 
                    key={index} 
                    color="light"
                    className="mr-2 mb-2 bg-primary-2" 
                    onClick={() => handleSelection(suggestion)}
                >
                    {suggestion.content}
                </Button>
            )}
            </section>
            <ResumeUpload open={showUploadResumeModal} setOpen={setShowUploadResumeModal} handleResumeUploaded={handleResumeUploaded} />
        </>
    )
}, (prevProps, nextProps) => {
    return prevProps.sendMessage === nextProps.sendMessage &&
           prevProps.assistantName === nextProps.assistantName &&
           JSON.stringify(prevProps.suggestions) === JSON.stringify(nextProps.suggestions);
});